import type HighchartsNS from 'highcharts';
import {
  currencyLabel,
  formatDate,
  formatLargeAmount,
  numberFormat,
  tooltipPercentRound,
} from '@nuxt-shared/lib/chart-format';
import type { ChartSeries, ChartWidgetTab, ChartWidgetTranslate } from '../types';

/**
 * Опции Highstock — порт конфигурации легаси
 * `components/aton/chart/templates/area.detail_v2024/script.js`.
 *
 * Цвета заданы явно: с 12-й версии дефолты Highcharts живут в CSS-переменных
 * `--highcharts-*` и переключаются по `prefers-color-scheme`, а переменных на
 * странице нет — без этого график уезжает в тёмную тему.
 */

/** Цвет горизонтальных линий сетки (`yAxis.gridLineColor` легаси). */
const GRID_LINE_COLOR = '#dbdbdb';

/** Цвет подписей осей — легаси-переменная `--content-text-color` в светлой теме. */
const AXIS_LABEL_COLOR = '#3e3c3c';

/** Цвет линии оси — дефолт Highcharts 9, на котором работает старая страница. */
const AXIS_LINE_COLOR = '#ccd6eb';

/** Короткие месяцы для подписей оси X — `A_CHART_T_S_*` легаси. */
const SHORT_MONTHS = [
  'Янв',
  'Фев',
  'Мар',
  'Апр',
  'Май',
  'Июн',
  'Июл',
  'Авг',
  'Сен',
  'Окт',
  'Ноя',
  'Дек',
];

/**
 * Минимальный шаг между засечками оси X в пикселях.
 *
 * Дефолт Highcharts — 100, у блока на деталке фонда стоит 80 (там ширина всей
 * страницы). В колонке статьи столько подписей не помещается, поэтому шаг
 * крупнее: примерно «27 Апр» + воздух.
 */
const TICK_PIXEL_INTERVAL = 130;

/** Зазор между легендой и областью построения в пикселях. */
const LEGEND_MARGIN = 24;

/** Прозрачность заливки под первой линией — `buildNonTradeZones` соседствует с ней в легаси. */
const FILL_OPACITY = 0.13;

interface ChartOptionsContext {
  series: ChartSeries[];
  tab: ChartWidgetTab;
  /** Код валюты для подписей — пустой, если ряд не валютный. */
  currency: string;
  /** Заливка градиентом под первой линией. */
  fill: boolean;
  t: ChartWidgetTranslate;
}

/** `#7000FF` → `rgba(112, 0, 255, alpha)`: Highcharts.Color в опциях недоступен. */
function withOpacity(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((char) => char + char)
          .join('')
      : value;
  const int = Number.parseInt(full, 16);

  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
}

/** Подпись значения оси Y — форматтеры легаси для доходности и для СЧА. */
function formatAxisValue(value: number, chartContext: ChartOptionsContext): string {
  const decimals = Number.isInteger(value) ? 0 : 2;

  if (chartContext.tab === 'scha') {
    const suffix = chartContext.currency
      ? ` ${currencyLabel(chartContext.currency)}`
      : '';
    const absolute = Math.abs(value);

    if (absolute >= 1_000_000_000) {
      return `${numberFormat(value / 1_000_000_000, 2, ',', ' ')} млрд${suffix}`;
    }

    if (absolute >= 1_000_000) {
      return `${numberFormat(value / 1_000_000, decimals, '.', ' ')} млн${suffix}`;
    }

    return `${numberFormat(value, decimals, '.', ' ')}${suffix}`;
  }

  // Легаси прячет подпись глубоко в минусе — там ось уходит ниже данных.
  if (value < -100) return '';

  return `${numberFormat(value, decimals, '.', ' ')} %`;
}

/** Строка одного ряда в тултипе: доходность в процентах и цена пая. */
function formatProfitRow(
  point: HighchartsNS.TooltipFormatterContextObject['points'][number],
  chartContext: ChartOptionsContext,
): string {
  const series = chartContext.series[point.series.index];
  const value = point.y ?? 0;
  // Первая видимая точка ряда — от неё Highcharts считает проценты при
  // `compare: 'percent'`, тултип легаси берёт её же.
  const start = point.series.points.find((item) => item.isInside);
  const percent = start?.y ? ((value - (start.y ?? 0)) / (start.y ?? 1)) * 100 : 0;
  const formatted = numberFormat(
    percent,
    tooltipPercentRound(percent),
    ',',
    ' ',
    true,
    true,
  );
  const price = series?.points[point.point.index]?.price ?? 0;
  const suffix = chartContext.currency ? ` ${currencyLabel(chartContext.currency)}` : '';
  const name =
    chartContext.series.length > 1
      ? `<p class="chart-widget__tip-name t3">${series?.name ?? ''}</p>`
      : '';

  return `<span class="chart-widget__tip-row">${name}
      <span class="chart-widget__tip-profit${formatted.includes('+') ? ' -plus' : ''}">
        <p class="t1">${formatted} %</p>
        <p class="t3">${chartContext.t('tab_profit')}</p>
      </span>
      <span class="chart-widget__tip-unit">
        <p class="chart-widget__tip-value t1">${numberFormat(price, 2, ',', ' ', true)}${suffix}</p>
        <p class="chart-widget__tip-title t3">${chartContext.t('unit_profit')}</p>
      </span>
    </span>`;
}

/** Строка ряда СЧА в тултипе. */
function formatSchaRow(
  point: HighchartsNS.TooltipFormatterContextObject['points'][number],
  chartContext: ChartOptionsContext,
): string {
  const series = chartContext.series[point.series.index];
  const suffix = chartContext.currency ? ` ${currencyLabel(chartContext.currency)}` : '';
  const name =
    chartContext.series.length > 1
      ? `<p class="chart-widget__tip-name t3">${series?.name ?? ''}</p>`
      : '';

  return `<span class="chart-widget__tip-row">${name}
      <span class="chart-widget__tip-unit">
        <p class="chart-widget__tip-value t1">${formatLargeAmount(point.y ?? 0)}${suffix}</p>
        <p class="chart-widget__tip-title t3">${chartContext.t('unit_scha')}</p>
      </span>
    </span>`;
}

/** HTML тултипа — разметка и порядок блоков повторяют легаси. */
function formatTooltip(
  context: HighchartsNS.TooltipFormatterContextObject,
  chartContext: ChartOptionsContext,
): string {
  const points = context.points ?? [];
  const first = points[0];

  if (!first) return '';

  const x = Number(context.x ?? first.x);

  // Участок без торгов: по макету у него отдельный тултип вместо цифр.
  const isNonTrade = points.some((point) =>
    chartContext.series[point.series.index]?.nonTradeDates.has(x),
  );

  if (isNonTrade) {
    return `<span class="chart-widget__tip -no-trade t2">${chartContext.t('no_trade')}</span>`;
  }

  const date = `<span class="chart-widget__tip-date">
      <p class="chart-widget__tip-label t2">${chartContext.t('tooltip_date')}</p>
      <p class="t2">${formatDate(x)}</p>
    </span>`;

  const rows = points
    .map((point) =>
      chartContext.tab === 'scha'
        ? formatSchaRow(point, chartContext)
        : formatProfitRow(point, chartContext),
    )
    .join('');

  return `<span class="chart-widget__tip">${date}${rows}</span>`;
}

/** Ряды для Highcharts: цвет, серые зоны и заливка под первой линией. */
function buildSeriesOptions(
  chartContext: ChartOptionsContext,
): HighchartsNS.SeriesOptionsType[] {
  return chartContext.series.map((series, index) => {
    // Заливку легаси даёт только первой линии, остальные — просто линии.
    const withFill = chartContext.fill && index === 0;

    return {
      type: withFill ? 'area' : 'line',
      name: series.name,
      data: series.points.map((point) => [point.x, point.y]),
      connectNulls: true,
      color: series.color,
      ...(withFill
        ? {
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 0.6 },
              stops: [
                [0, withOpacity(series.color, FILL_OPACITY)],
                [1, withOpacity(series.color, 0)],
              ] as [number, string][],
            },
          }
        : {}),
      ...(series.zones.length
        ? {
            zoneAxis: 'x',
            zones: series.zones as HighchartsNS.SeriesZonesOptionsObject[],
          }
        : {}),
      marker: { states: { hover: { lineColor: series.color } } },
    } as HighchartsNS.SeriesOptionsType;
  });
}

export function buildChartOptions(
  chartContext: ChartOptionsContext,
): HighchartsNS.Options {
  return {
    credits: { enabled: false },
    chart: {
      backgroundColor: 'transparent',
      plotBorderWidth: 0,
      // Левое поле не задаём: Highcharts считает его по ширине подписей оси Y.
      // Легаси ставил `marginLeft: 0` и выносил подписи левее блока, но в колонке
      // статьи слева только отступ карточки — «7,00 млрд руб.» из неё торчало.
      style: { fontFamily: 'inherit' },
    },
    time: { useUTC: true, timezoneOffset: -3 * 60 },
    lang: { shortMonths: SHORT_MONTHS, months: SHORT_MONTHS },
    rangeSelector: { enabled: false },
    navigator: { enabled: false },
    scrollbar: { enabled: false },
    legend: {
      // Легенда нужна только когда линий больше одной.
      enabled: chartContext.series.length > 1,
      align: 'left',
      layout: 'horizontal',
      verticalAlign: 'top',
      // Зазор до области построения: верхняя подпись оси Y стоит на её границе
      // и наполовину выступает вверх. Легаси держал `margin: 0`, потому что
      // подписи были левее блока; теперь они под легендой и без зазора слипаются.
      margin: LEGEND_MARGIN,
      padding: 0,
      itemDistance: 10,
      itemMarginTop: 12,
      itemStyle: { color: AXIS_LABEL_COLOR, fontFamily: 'inherit', fontWeight: 'normal' },
      itemHoverStyle: { color: AXIS_LABEL_COLOR },
    },
    plotOptions: {
      series: {
        // Проценты на оси считает сам Highcharts — от первой видимой точки.
        compare: chartContext.tab === 'scha' ? undefined : 'percent',
        compareStart: true,
        dataGrouping: { enabled: false },
        lineWidth: 1,
        states: {
          hover: { halo: { size: 0 }, lineWidth: 1 },
          inactive: { opacity: 1 },
        },
      },
    },
    tooltip: {
      animation: true,
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderRadius: 0,
      padding: 0,
      hideDelay: 0,
      outside: false,
      shadow: false,
      shape: 'callout',
      shared: true,
      split: false,
      useHTML: true,
      style: { fontFamily: 'inherit', whiteSpace: 'normal' },
      formatter(this: HighchartsNS.TooltipFormatterContextObject) {
        return formatTooltip(this, chartContext);
      },
    },
    xAxis: {
      type: 'datetime',
      crosshair: false,
      showLastLabel: true,
      minTickInterval: 24 * 3600 * 1000,
      startOnTick: false,
      gridLineWidth: 0,
      tickWidth: 0,
      lineColor: AXIS_LINE_COLOR,
      // Виджет стоит в колонке статьи, а не во всю ширину страницы, как блок
      // на деталке фонда: при частых засечках подписи налезали друг на друга
      // и переносились на две строки. Даём каждой подписи заметно больше места.
      tickPixelInterval: TICK_PIXEL_INTERVAL,
      dateTimeLabelFormats: {
        // Год в дневных подписях не нужен — он есть в соседних месячных
        // засечках, а строка «27 Апр 26» вдвое шире и как раз и слипалась.
        millisecond: '%e %b',
        second: '%e %b',
        minute: '%e %b',
        hour: '%e %b',
        day: '%e %b',
        week: '%e %b',
        month: '%b %y',
        year: '%Y',
      },
      labels: {
        // Подпись переносится по словам, если не помещается: перенос и давал
        // «этажи» из дат. Запрещаем перенос и разрешаем наклон, когда тесно.
        autoRotation: [0, -45],
        style: {
          fontFamily: 'inherit',
          color: AXIS_LABEL_COLOR,
          whiteSpace: 'nowrap',
        },
      },
    },
    yAxis: {
      showLastLabel: true,
      opposite: false,
      allowDecimals: true,
      gridLineColor: GRID_LINE_COLOR,
      lineColor: AXIS_LINE_COLOR,
      labels: {
        enabled: true,
        align: 'right',
        y: 4,
        x: -8,
        style: { fontFamily: 'inherit', color: AXIS_LABEL_COLOR },
        formatter(this: HighchartsNS.AxisLabelsFormatterContextObject) {
          return formatAxisValue(Number(this.value), chartContext);
        },
      },
    },
    series: buildSeriesOptions(chartContext),
  };
}
